import React, { useState, useEffect } from 'react';

const UserManagement = ({ onCreateUser, onGetUsersList, onUpdateUser, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    userFirstName: '',
    userLastName: '',
    eMailId: '',
    password: '',
    status: 1,
    userRole: 1,
    managerId: null,
    accountID: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await onGetUsersList();
      if (result.success) {
        setUsers(result.data);
      } else {
        console.error('Failed to load users:', result.error);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      userFirstName: '',
      userLastName: '',
      eMailId: '',
      password: '',
      status: 1,
      userRole: 1,
      managerId: null,
      accountID: ''
    });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (editingUser) {
        // Update existing user
        const updateData = {
          ...formData,
          userId: editingUser.userId
        };
        console.log('Updating user with data:', updateData);
        result = await onUpdateUser(updateData);
        console.log('Update result:', result);
      } else {
        // Create new user
        console.log('Creating user with data:', formData);
        result = await onCreateUser(formData);
        console.log('Create result:', result);
      }

      if (result.success) {
        resetForm();
        loadUsers(); // Reload users list
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Failed to save user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    console.log('Editing user:', user);
    setEditingUser(user);
    setFormData({
      userFirstName: user.userFirstName || '',
      userLastName: user.userLastName || '',
      eMailId: user.eMailId || '',
      password: '', // Don't populate password for security
      status: user.status || 1,
      userRole: user.userRole || 1,
      managerId: user.managerId || null,
      accountID: user.accountID || ''
    });
    setShowCreateForm(true);
    console.log('Form data set for editing:', {
      userFirstName: user.userFirstName || '',
      userLastName: user.userLastName || '',
      eMailId: user.eMailId || '',
      password: '', // Don't populate password for security
      status: user.status || 1,
      userRole: user.userRole || 1,
      managerId: user.managerId || null,
      accountID: user.accountID || ''
    });
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      // Update user status to suspended (3) instead of actual deletion
      const result = await onUpdateUser({
        ...userToDelete,
        status: 3 // Suspended
      });

      if (result.success) {
        loadUsers(); // Reload users list
        setShowDeleteConfirm(false);
        setUserToDelete(null);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      alert('Please select users first');
      return;
    }

    setLoading(true);
    try {
      for (const userId of selectedUsers) {
        const user = users.find(u => u.userId === userId);
        if (user) {
          let newStatus;
          switch (action) {
            case 'activate':
              newStatus = 1;
              break;
            case 'freeze':
              newStatus = 2;
              break;
            case 'suspend':
              newStatus = 3;
              break;
            default:
              continue;
          }

          await onUpdateUser({
            ...user,
            status: newStatus
          });
        }
      }
      
      setSelectedUsers([]);
      loadUsers(); // Reload users list
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Failed to perform bulk action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.userId));
    }
  };

  const getUserRoleName = (userRole) => {
    switch (userRole) {
      case 1: return 'Trader';
      case 2: return 'Trader / Manager';
      case 3: return 'Admin';
      default: return 'User';
    }
  };

  const getUserStatusName = (status) => {
    switch (status) {
      case 1: return 'Active';
      case 2: return 'Frozen';
      case 3: return 'Suspended';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'text-green-400';
      case 2: return 'text-yellow-400';
      case 3: return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  // Filter users based on search term and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.userFirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userLastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.eMailId?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.userRole === parseInt(filterRole);
    const matchesStatus = filterStatus === 'all' || user.status === parseInt(filterStatus);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Manager candidates: users with role Trader / Manager (2) or Admin (3)
  const managerCandidates = users.filter(u => u.userRole === 2 || u.userRole === 3);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-white">User Management</h3>
          <p className="text-dark-400 text-sm mt-1">Manage trading platform users</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-primary-600 text-white px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base w-full sm:w-auto"
          >
            + Add New User
          </button>
          {selectedUsers.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('activate')}
                className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
              >
                Activate ({selectedUsers.length})
              </button>
              <button
                onClick={() => handleBulkAction('freeze')}
                className="bg-yellow-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-yellow-700 transition-colors"
              >
                Freeze ({selectedUsers.length})
              </button>
              <button
                onClick={() => handleBulkAction('suspend')}
                className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
              >
                Suspend ({selectedUsers.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Search Users</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Filter by Role</label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            >
              <option value="all">All Roles</option>
              <option value="1">Trader</option>
              <option value="2">Trader / Manager</option>
              <option value="3">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-dark-300 font-medium mb-2 text-sm">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm"
            >
              <option value="all">All Status</option>
              <option value="1">Active</option>
              <option value="2">Frozen</option>
              <option value="3">Suspended</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterRole('all');
                setFilterStatus('all');
              }}
              className="w-full bg-dark-700 text-dark-200 px-3 py-2 rounded-lg text-sm font-medium hover:bg-dark-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
          <div className="flex justify-between items-center mb-4 sm:mb-5">
            <div>
              <h4 className="text-lg sm:text-xl font-semibold text-white">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h4>
              {editingUser && (
                <p className="text-blue-400 text-sm mt-1">
                  Editing: {editingUser.userFirstName} {editingUser.userLastName} (ID: {editingUser.userId})
                </p>
              )}
            </div>
            <button
              onClick={resetForm}
              className="text-dark-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  First Name *
                </label>
                <input
                  type="text"
                  name="userFirstName"
                  value={formData.userFirstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="userLastName"
                  value={formData.userLastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            </div>

            <div>
              <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                Email Address *
              </label>
              <input
                type="email"
                name="eMailId"
                value={formData.eMailId}
                onChange={handleInputChange}
                required
                className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
              />
            </div>

            {!editingUser && (
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Password *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
            )}

            {editingUser && (
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  <span className="font-medium">Edit Mode:</span> Password field is hidden for security. 
                  Leave password field empty to keep current password, or enter a new password to change it.
                </p>
                <div className="mt-2">
                  <label className="block text-dark-300 font-medium mb-2 text-sm">
                    New Password (optional)
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter new password to change it"
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Account ID
                </label>
                <input
                  type="text"
                  name="accountID"
                  value={formData.accountID}
                  onChange={handleInputChange}
                  placeholder="Optional account identifier"
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  User Status *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>Active</option>
                  <option value={2}>Frozen</option>
                  <option value={3}>Suspended</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  User Role *
                </label>
                <select
                  name="userRole"
                  value={formData.userRole}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value={1}>Trader</option>
                  <option value={2}>Trader / Manager</option>
                  <option value={3}>Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">
                  Manager ID
                </label>
                <select
                  name="managerId"
                  value={formData.managerId ?? ''}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      managerId: e.target.value === '' ? null : parseInt(e.target.value, 10)
                    }))
                  }
                  className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                >
                  <option value="">Select a manager (optional)...</option>
                  {managerCandidates.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.userId} - {m.userFirstName} {m.userLastName} ({getUserRoleName(m.userRole)})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-primary-600 text-white px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-dark-700 text-dark-200 px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-dark-600 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="bg-dark-800 border border-dark-700 rounded-xl overflow-hidden shadow-lg">
        <div className="p-4 sm:p-6 border-b border-dark-700">
          <h4 className="text-lg sm:text-xl font-semibold text-white">Users List</h4>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-dark-400">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-dark-400">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">User ID</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Name</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Email</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Role</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Status</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Account ID</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Manager ID</th>
                  <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.userId} className="border-b border-dark-700 hover:bg-dark-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.userId)}
                        onChange={() => handleSelectUser(user.userId)}
                        className="rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-blue-400 font-medium">
                      {user.userId}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white">
                      {user.userFirstName} {user.userLastName}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-dark-300 truncate max-w-xs">
                      {user.eMailId}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-white">
                      {getUserRoleName(user.userRole)}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <span className={getStatusColor(user.status)}>
                        {getUserStatusName(user.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-purple-400">
                      {user.accountID || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base text-dark-300">
                      {user.managerId || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm sm:text-base">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-primary-400 hover:text-primary-300 transition-colors"
                          title="Edit User"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Delete User"
                        >
                          🗑️
                        </button>
                        <button
                          onClick={() => handleBulkAction('activate')}
                          className="text-green-400 hover:text-green-300 transition-colors"
                          title="Activate User"
                        >
                          ✅
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-dark-400">No users found matching your criteria</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Confirm Delete</h3>
            <p className="text-dark-300 mb-6">
              Are you sure you want to suspend user "{userToDelete?.userFirstName} {userToDelete?.userLastName}"? 
              This action can be reversed later.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Suspending...' : 'Suspend User'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUserToDelete(null);
                }}
                className="bg-dark-700 text-dark-200 px-4 py-2 rounded-lg font-medium hover:bg-dark-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
